import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Package, Calendar, DollarSign, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const OrderHistoryPage = ({ user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [cycleSummary, setCycleSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadOrderHistory();
    loadCycleSummary();
  }, [user]);

  const loadOrderHistory = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${backendUrl}/api/orders/my-orders`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Load orders error:', error);
      toast.error('Failed to load order history');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCycleSummary = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${backendUrl}/api/orders/current-cycle`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCycleSummary(data);
      }
    } catch (error) {
      console.error('Load cycle summary error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      fulfilled: { color: 'bg-blue-100 text-blue-800', label: 'Fulfilled' },
      invoiced: { color: 'bg-purple-100 text-purple-800', label: 'Invoiced' },
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  const creditPercentage = cycleSummary 
    ? (cycleSummary.current_cycle_total / cycleSummary.credit_limit) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="flex items-center text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Store
            </Button>
            <h1 className="text-xl font-bold text-slate-900">Order History</h1>
            <div></div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Current Billing Cycle Summary */}
        {cycleSummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Current Cycle Total</p>
                    <p className="text-2xl font-bold text-slate-900">${cycleSummary.current_cycle_total.toFixed(2)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Credit Remaining</p>
                    <p className="text-2xl font-bold text-green-600">${cycleSummary.remaining_credit.toFixed(2)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Orders This Cycle</p>
                    <p className="text-2xl font-bold text-slate-900">{cycleSummary.orders_count}</p>
                  </div>
                  <Package className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Next Billing Date</p>
                    <p className="text-lg font-bold text-slate-900">
                      {cycleSummary.next_billing_date ? new Date(cycleSummary.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Credit Limit Bar */}
        {cycleSummary && (
          <Card className="mb-8">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Credit Limit Usage</span>
                <span className="text-sm font-semibold">
                  ${cycleSummary.current_cycle_total.toFixed(2)} / ${cycleSummary.credit_limit.toFixed(2)}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full transition-all ${
                    creditPercentage > 90 ? 'bg-red-500' :
                    creditPercentage > 70 ? 'bg-orange-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(creditPercentage, 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {creditPercentage.toFixed(1)}% used • ${cycleSummary.remaining_credit.toFixed(2)} remaining
              </p>
            </CardContent>
          </Card>
        )}

        {/* Order History */}
        <Card>
          <CardHeader>
            <CardTitle>All Orders</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-center text-slate-500 py-8">Loading orders...</p>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 text-lg mb-4">No orders yet</p>
                <Button onClick={() => navigate('/')}>
                  Start Shopping
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-semibold text-slate-900">
                          Order #{order.id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-sm text-slate-600">
                          {new Date(order.order_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(order.status)}
                        <p className="text-lg font-bold text-green-600 mt-1">
                          ${order.total_amount.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="space-y-2 mb-4">
                      {order.items && order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-slate-50 p-3 rounded">
                          <span className="text-slate-700">{item.product_name}</span>
                          <span className="text-slate-900 font-medium">
                            {item.quantity} × ${item.unit_price.toFixed(2)} = ${item.total_price.toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-between items-center text-sm pt-3 border-t border-slate-200">
                      <div className="flex gap-4 text-slate-600">
                        <span>Subtotal: ${order.subtotal.toFixed(2)}</span>
                        <span>GST: ${order.tax.toFixed(2)}</span>
                      </div>
                      {order.notes && (
                        <p className="text-xs text-slate-500 italic">Note: {order.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrderHistoryPage;
